import { useState } from "react";
import { Divider, Stack, useTheme } from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { type Dayjs } from "dayjs";
import dayjsUtc from "dayjs/plugin/utc";
import { AnchoredPopover, Button, FoodsIcon, FoodsRadioList, Typography } from "@foodpilot/foods";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
    AUDIT_LOG_PERIODS,
    instantToUtcWallClock,
    isInvertedAuditLogWindow,
    resolveAuditLogWindow,
    utcWallClockToInstant,
    type AuditLogCustomWindow,
    type AuditLogPeriod,
} from "./auditLogPresentation";
import { useAuditLogLabels, type AuditLogLabels } from "./useAuditLogLabels";

dayjs.extend(dayjsUtc);

export const auditLogWindowPickerValue = (stored: string | null): Dayjs | null =>
    stored === null ? null : dayjs.utc(instantToUtcWallClock(stored));

export const auditLogWindowPickerCommit = (value: Dayjs | null): string | null =>
    value === null ? null : utcWallClockToInstant(value.format("YYYY-MM-DDTHH:mm:ss"));

type AuditLogWindowPickerProps = {
    period: AuditLogPeriod;
    onPeriodChange: (period: AuditLogPeriod) => void;
    appliedWindow: AuditLogCustomWindow;
    draftWindow: AuditLogCustomWindow;
    onDraftWindowChange: (window: AuditLogCustomWindow) => void;
    onApply: () => void;
    isCompact: boolean;
};

type PeriodOption = {
    id: AuditLogPeriod;
    name: string;
};

const DEFAULT_PERIOD: AuditLogPeriod = "day";

const buildPeriodLabels = (t: TFunction): Record<AuditLogPeriod, string> => ({
    day: t("settings.audit_log.period.day", "Last 24 h"),
    week: t("settings.audit_log.period.week", "Last 7 days"),
    month: t("settings.audit_log.period.month", "Last 30 days"),
    all: t("settings.audit_log.period.all", "All history"),
    custom: t("settings.audit_log.period.custom_option", "Precise window"),
});

const describeAuditLogWindow = (window: AuditLogCustomWindow, labels: AuditLogLabels, t: TFunction): string => {
    const stamp = (value: string | null): string | null => (value === null ? null : labels.shortUtc(value));
    const from = stamp(window.from);
    const to = stamp(window.to);

    if (from !== null && to !== null) {
        return t("settings.audit_log.period.range", "{{from}} → {{to}} (UTC)", { from, to });
    }

    if (from !== null) {
        return t("settings.audit_log.period.since", "Since {{from}} (UTC)", { from });
    }

    if (to !== null) {
        return t("settings.audit_log.period.until", "Until {{to}} (UTC)", { to });
    }

    return t("settings.audit_log.period.unbounded", "No bound — all history");
};

export const AuditLogWindowPicker = (props: AuditLogWindowPickerProps) => {
    const { period, onPeriodChange, appliedWindow, draftWindow, onDraftWindowChange, onApply, isCompact } = props;
    const { t } = useTranslation();
    const labels = useAuditLogLabels();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    const periodLabels = buildPeriodLabels(t);
    const close = () => setAnchor(null);

    return (
        <>
            <Button
                variant="secondary"
                startIcon={<FoodsIcon icon="calendar" size={2} />}
                endIcon={<FoodsIcon icon="arrowDownShort" size={2} />}
                onClick={(event) => setAnchor(event.currentTarget)}
                sx={{ justifyContent: "space-between", width: "100%", overflow: "hidden" }}
            >
                <Typography variant="small-medium" noWrap>
                    {period === "custom" ? describeAuditLogWindow(appliedWindow, labels, t) : periodLabels[period]}
                </Typography>
            </Button>

            <AnchoredPopover open={anchor !== null} anchor={anchor} onClose={close} anchorPosition="rightside">
                <AuditLogWindowPickerContent
                    key={anchor === null ? "closed" : "open"}
                    period={period}
                    onPeriodChange={onPeriodChange}
                    draftWindow={draftWindow}
                    onDraftWindowChange={onDraftWindowChange}
                    onApply={onApply}
                    isCompact={isCompact}
                    onClose={close}
                />
            </AnchoredPopover>
        </>
    );
};

type AuditLogWindowPickerContentProps = {
    period: AuditLogPeriod;
    onPeriodChange: (period: AuditLogPeriod) => void;
    draftWindow: AuditLogCustomWindow;
    onDraftWindowChange: (window: AuditLogCustomWindow) => void;
    onApply: () => void;
    isCompact: boolean;
    onClose: () => void;
};

const AuditLogWindowPickerContent = (props: AuditLogWindowPickerContentProps) => {
    const { period, onPeriodChange, draftWindow, onDraftWindowChange, onApply, isCompact, onClose } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const labels = useAuditLogLabels();
    const [draftPeriod, setDraftPeriod] = useState<AuditLogPeriod>(period);

    const periodLabels = buildPeriodLabels(t);
    const options: PeriodOption[] = AUDIT_LOG_PERIODS.map((id) => ({ id, name: periodLabels[id] }));
    const isInverted = draftPeriod === "custom" && isInvertedAuditLogWindow(draftWindow);
    const resolved = resolveAuditLogWindow(draftPeriod, new Date(), draftWindow);

    const preview = (
        <Stack
            gap={theme.spacing(0.5)}
            sx={{
                backgroundColor: theme.palette.grey[50],
                borderRadius: theme.spacing(0.5),
                padding: theme.spacing(1),
            }}
        >
            <Typography variant="small-regular" color="text.secondary">
                {t("settings.audit_log.period.preview", "Requested window")}
            </Typography>
            <Typography variant="small-semibold" color={isInverted ? theme.palette.warning.dark : "text.primary"}>
                {isInverted ?
                    t("settings.audit_log.period.inverted", "The end date precedes the start date.")
                :   describeAuditLogWindow({ from: resolved.from ?? null, to: resolved.to ?? null }, labels, t)}
            </Typography>
        </Stack>
    );

    const choose = (next: AuditLogPeriod) => {
        setDraftPeriod(next);

        if (next !== "custom") {
            onPeriodChange(next);
            onClose();
        }
    };

    const apply = () => {
        onPeriodChange("custom");
        onApply();
        onClose();
    };

    const reset = () => {
        onDraftWindowChange({ from: null, to: null });
        onPeriodChange(DEFAULT_PERIOD);
        onClose();
    };

    const bound = (key: "from" | "to", label: string) => {
        const stored = draftWindow[key];

        return (
            <DateTimePicker
                label={label}
                value={auditLogWindowPickerValue(stored)}
                onChange={(value) => onDraftWindowChange({ ...draftWindow, [key]: auditLogWindowPickerCommit(value) })}
                ampm={false}
                format="DD/MM/YYYY HH:mm"
                timezone="UTC"
                slotProps={{
                    textField: { size: "small", fullWidth: true },
                    popper: {
                        placement: "left",
                        modifiers: [
                            { name: "flip", enabled: false },
                            { name: "offset", options: { offset: [0, 24] } },
                            { name: "preventOverflow", options: { padding: 16 } },
                        ],
                    },
                }}
            />
        );
    };

    return (
        <Stack
            gap={theme.spacing(2)}
            width={theme.spacing(isCompact ? 32 : 42)}
            sx={{ maxHeight: "70vh", overflowY: "auto", padding: theme.spacing(2) }}
        >
            <Stack gap={theme.spacing(1)}>
                <Typography variant="small-semibold">{t("settings.audit_log.period.presets", "Period")}</Typography>
                <FoodsRadioList<PeriodOption>
                    selectedItem={options.find((option) => option.id === draftPeriod)}
                    values={options}
                    onChange={(option) => choose(option.id)}
                    getId={(option) => option.id}
                    getName={(option) => option.name}
                    FormControlProps={{ sx: { flexDirection: "column" } }}
                    FormControlLabelProps={{ sx: { marginY: theme.spacing(-0.25) } }}
                />
            </Stack>

            {draftPeriod !== "custom" && preview}

            {draftPeriod === "custom" && (
                <>
                    <Divider />

                    <Stack gap={theme.spacing(2)}>
                        <Stack gap={theme.spacing(0.5)}>
                            <Typography variant="small-semibold">
                                {t("settings.audit_log.period.exact", "Window bounds")}
                            </Typography>
                            <Typography variant="small-regular" color="text.secondary">
                                {t(
                                    "settings.audit_log.period.exact_helper_bounds",
                                    "Times are read in UTC, like the rest of this screen. A bound left empty stays open.",
                                )}
                            </Typography>
                        </Stack>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Stack gap={theme.spacing(2)}>
                                {bound("from", t("settings.audit_log.period.from_utc", "From (UTC)"))}
                                {bound("to", t("settings.audit_log.period.to_utc", "To (UTC)"))}
                            </Stack>
                        </LocalizationProvider>

                        {preview}
                    </Stack>

                    <Divider />

                    <Stack direction="row" justifyContent="space-between" gap={theme.spacing(1)}>
                        <Button variant="black-text" onClick={reset}>
                            {t("settings.audit_log.period.reset", "Reset")}
                        </Button>
                        <Button variant="primary" onClick={apply} disabled={isInverted}>
                            {t("settings.audit_log.period.apply", "Apply")}
                        </Button>
                    </Stack>
                </>
            )}
        </Stack>
    );
};
